import { gql } from '@apollo/client';
import { getClient } from '../apollo/client';

const IMAGE_UPLOAD_CONFIG = gql`
  query imageUploadConfig {
    imageUploadConfig {
      maxSize
      allowedFormats
      uploadUrl
    }
  }
`;

const GENERATE_UPLOAD_URL = gql`
  mutation generateUploadUrl($input: GenerateUploadUrlInput!) {
    generateUploadUrl(input: $input) {
      objectPath
      signedUrl
      expiresAt
    }
  }
`;

let cachedConfig: { maxSize: number; allowedFormats: string[]; uploadUrl: string | null } | null = null;

export async function getImageUploadConfig() {
  if (!cachedConfig) {
    const { data } = await getClient().query({
      query: IMAGE_UPLOAD_CONFIG,
      fetchPolicy: 'network-only',
    });
    cachedConfig = data.imageUploadConfig;
  }
  return cachedConfig!;
}

export type UploadImageOptions = {
  feature: string;
  ownerId?: string;
  contentType?: string;
  onProgress?: (percent: number) => void;
};

export async function uploadImage(file: File, options: UploadImageOptions): Promise<string> {
  const config = await getImageUploadConfig();

  if (file.size > config.maxSize) {
    throw new Error(`File exceeds maximum size of ${config.maxSize} bytes`);
  }

  const contentType = options.contentType || file.type || 'application/octet-stream';

  const { data } = await getClient().mutate({
    mutation: GENERATE_UPLOAD_URL,
    variables: {
      input: {
        feature: options.feature,
        ownerId: options.ownerId,
        contentType,
      },
    },
  });

  const { objectPath, signedUrl } = data.generateUploadUrl;

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', contentType);

    if (options.onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          options.onProgress!(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });

  return objectPath;
}

export async function uploadDataUrl(dataUrl: string, options: UploadImageOptions): Promise<string> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], 'upload.jpg', { type: blob.type || 'image/jpeg' });
  return uploadImage(file, options);
}
